import React, { createContext, useState, useEffect, useContext } from 'react';
import { LoginRequest, UserSession, AuthContextType } from '../types/auth';
import { apiRequest } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserSession | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const validateSession = async () => {
      const storedSession = localStorage.getItem('userSession');
      if (storedSession) {
        try {
          const session = JSON.parse(storedSession) as UserSession;
          
          // Check if token is expired based on timestamp
          const expiresAt = new Date(session.expires_at);
          if (expiresAt > new Date()) {
            try {
              // Validate token with backend
              await apiRequest<UserSession>('/auth/validate', {
                method: 'GET',
              });
              // If we get here, token is valid
              setUser(session);
              setIsAuthenticated(true);
            } catch (error) {
              // Handle network errors and authentication failures
              if (error instanceof Error && (
                error.message.includes('Unable to connect to server') ||
                error.message.includes('Network connection failed') ||
                error.message.includes('Failed to fetch')
              )) {
                // Network error - backend is unreachable
                console.error('Backend unreachable during session validation:', error);
                localStorage.removeItem('userSession');
                // Redirect will be handled by apiRequest, but we ensure cleanup here
                return;
              } else {
                // Token is invalid according to server or other error
                console.error('Token validation failed:', error);
                localStorage.removeItem('userSession');
                window.location.href = '/login';
                return;
              }
            }
          } else {
            // Token expired, remove from storage
            localStorage.removeItem('userSession');
            window.location.href = '/login';
            return;
          }
        } catch (error) {
          console.error('Error parsing stored session:', error);
          localStorage.removeItem('userSession');
          window.location.href = '/login';
          return;
        }
      }
      setIsLoading(false);
    };
    
    validateSession();
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      const data = await apiRequest<UserSession>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
        authorized: false, // No auth token needed for login
      });
      
      localStorage.setItem('userSession', JSON.stringify(data));
      setUser(data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = (): void => {
    localStorage.removeItem('userSession');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 